import axios from "@/utils/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const SUSPENSION_REQUEST_DATA_PARAMS = {
  relationships: [
    "requestedBy",
    "scaledBy",
    "scaledTo",
    "files",
    "formalityRequest.applicant",
    "formalityRequest.flowExecution",
    "formalityRequest.flowExecution.activeNodeExecution",
    "formalityRequest.flowExecution.activeNodeExecution.node",
    "formalityRequest.flowExecution.activeNodeExecution.revision",
    "formalityRequest.flowExecution.activeNodeExecution.node.expiration",
  ].join(","),
};
export interface UpdateDeadlineSuspensionConfigType {
  configs: Array<{
    is_global: boolean;
    max_days: number;
    tag_id?: string;
    process_id?: string;
    id?: string;
  }>;
}

interface UpdateDeadlineSuspensionRequest {
  action: "APPROVE" | "DENY" | "SCALE";
  approved_days?: number;
  observation?: string;
  scaled_to?: string;
}

interface CreateSuspensionRequest {
  formality_request_id: string;
  requested_days: number;
  observation: string;
}

export interface SuspensionRequest {
  id: string;
  formality_request_id?: string;
  observation?: string;
  created_at?: string;
  updated_at?: string;
  approved_at: string | null;
  denied_at: string | null;
  scaled_at: string | null;
  scaled_to: string | null;
  files?: Array<{
    id: string;
    name: string;
    url: string;
    size: number;
  }>;
  formality_request?: {
    flow_execution_id?: string;
    requires_leader_approval?: boolean;
    analyst_deadline: string | null;
    applicant_deadline: string | null;
    applicant?: {
      id: string;
      first_name: string;
      last_name: string;
      type: string;
      email: string;
    };
    flowExecution?: {
      active_node_execution?: {
        node?: {
          display_name?: string;
          id?: string;
          flow_id?: string;
          type?: string;
        };
      };
    };
  };
}
export interface SuspensionInfo {
  data?: {
    formality_request_id?: string;
    requests?: Array<SuspensionRequest>;
    can_create_new?: boolean;
    max_days?: number;
    max_suspensions_per_formality?: number;
    current_count?: number;
    approved_count?: number;
    available_suspension_options?: Array<object>;
  };
}

export interface SuspensionConfigType {
  data?: Array<object>;
  last_bulk_operation?: {
    performed_at?: string;
    user?: {
      full_name?: string;
      id?: string;
      last_name?: string;
      name?: string;
    };
  };
}

export const useUpdateDeadlineSuspension = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateDeadlineSuspensionConfigType) => {
      const response = await axios.put(
        "/admin/v1/node-deadline-suspension-configs/bulk",
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "tags",
          {
            params: {
              page: 1,
              per_page: 20,
              relationships:
                "processes,processes.deadlineSuspensionConfig,globalDeadlineSuspensionConfig",
            },
          },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["suspension-requests-configs"],
      });
    },
  });
};

export const useGetSuspensionRequests = (
  id?: string,
  refreshInterval = 0,
  params = {},
) => {
  return useQuery({
    queryKey: ["suspension-requests", id, params],
    queryFn: async (): Promise<SuspensionInfo> => {
      const response = await axios.get(
        `/admin/v1/node-deadline-suspension-requests/formality-request/${id}`,
        { params },
      );
      return response.data;
    },
    ...(refreshInterval > 0 ? { refetchInterval: refreshInterval } : {}),
    enabled: !!id,
  });
};

export const useCreateSuspensionRequest = () => {
  return useMutation({
    mutationFn: async (payload: CreateSuspensionRequest) => {
      const response = await axios.post(
        "/admin/v1/node-deadline-suspension-requests",
        payload,
      );
      return response.data;
    },
  });
};

export const useUpdateSuspensionRequest = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateDeadlineSuspensionRequest) => {
      const response = await axios.put(
        `/admin/v1/node-deadline-suspension-requests/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["suspension-requests", id],
      });
    },
  });
};

export const useGetSuspensionConfigs = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["suspension-requests-configs"],
    queryFn: async (): Promise<SuspensionConfigType> => {
      const response = await axios.get(
        `/admin/v1/node-deadline-suspension-configs`,
      );
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes for cache reuse
  });
};
